import { isDefined, perpendicular } from '../utils';

export default class SortGrid {
  setLines(vertical, horizontal) {
    this.vertical = vertical;
    this.horizontal = horizontal;
  }

  sortLines(lines, type) {
    return lines.sort((a, b) => {
      const aEnd = a.endPosition || a.startPosition;
      const bEnd = b.endPosition || b.startPosition;

      if (aEnd === bEnd) {
        const aPerpMaxLine = this.getLineInfo(
          Math.max(a.drawFromLine, a.drawToLine), // TODO handle strings
          perpendicular(type),
        );
        const bPerpMaxLine = this.getLineInfo(
          Math.max(b.drawFromLine, b.drawToLine), // TODO handle strings
          perpendicular(type),
        );
        let aPerpMax, bPerpMax;
        if (isDefined(aPerpMaxLine)) {
          aPerpMax = aPerpMaxLine.endPosition || aPerpMaxLine.startPosition;
        } else {
          aPerpMax = Math.max(a.drawFromPosition, a.drawToPosition);
        }

        if (isDefined(bPerpMaxLine)) {
          bPerpMax = bPerpMaxLine.startPosition;
        } else {
          bPerpMax = Math.max(b.drawFromPosition, b.drawToPosition);
        }
        return aPerpMax > bPerpMax ? 1 : -1;
      }
      return aEnd > bEnd ? 1 : -1;
    });
  }

  getLineInfo(index, type, start = true) {
    if (!isDefined(index) || isNaN(index)) return null;
    const frames = this.sortLines(
      this[type].filter(line => !line.segmentGroup),
      type,
      start,
    );
    return frames[index];
  }

  validateLines({ vertical = [], horizontal = [], circle = [] }) {
    this.setLines(horizontal, vertical);
    const errors = [];
    try {
      // Run check on straight lines
      errors.push(
        ...[...horizontal, ...vertical].map(
          (
            {
              startPosition,
              drawFromPosition,
              drawToPosition,
              drawFromLine,
              drawToLine,
              extend,
            },
            index,
          ) => {
            const type = index < horizontal.length ? 'horizontal' : 'vertical';
            const i = type === 'vertical' ? index - horizontal.length : index;
            const e = [];
            if (!isDefined(startPosition)) {
              e.push(
                new Error(
                  `Start position is not defined for ${type} line at index ${i}`,
                ),
              );
            }

            if (!isDefined(drawFromPosition) && !isDefined(drawFromLine)) {
              e.push(
                new Error(
                  `Draw start is not defined for ${type} line at index ${i}. Please provide a drawFromPosition or drawFromLine property`,
                ),
              );
            }

            if (!isDefined(drawToPosition) && !isDefined(drawToLine)) {
              e.push(
                new Error(
                  `Draw end is not defined for ${type} line at index ${i}. Please provide a drawToPosition or drawToLine property`,
                ),
              );
            }

            if (isDefined(extend) && extend && !isDefined(drawToLine)) {
              e.push(
                new Error(
                  `Extend error found for ${type} line at index ${i}. Extend must be used with drawToLine property. Extend is used to say the line should have the initial length of where the line is but also should grow if the line moves.`,
                ),
              );
            }

            return e;
          },
        ),
      );

      try {
        // Check if there are any dependency errors by sorting lines
        this.sortLines(horizontal, 'horizontal');
        this.sortLines(vertical, 'vertical');
      } catch (error) {
        errors.push(error);
      }

      // Run check on circle lines
      circle.forEach(
        (
          {
            startPositionX,
            startPositionY,
            drawFromAngle,
            drawAngle,
            startBoundaryX,
            endBoundaryX,
            startBoundaryLineX,
            endBoundaryLineX,
            startBoundaryY,
            endBoundaryY,
            startBoundaryLineY,
            endBoundaryLineY,
          },
          index,
        ) => {
          const e = [];
          if (!isDefined(drawFromAngle)) {
            e.push(
              new Error(
                `The start angle property drawFromAngle is not defined at circle index ${index}. This is where the line angle starts in the circle`,
              ),
            );
          }

          if (!isDefined(drawAngle)) {
            e.push(
              new Error(
                `The degrees of the circle to draw property drawAngle is not defined at circle index ${index}. This is the angle in degrees to draw from 0 to 360`,
              ),
            );
          }

          if (!isDefined(startPositionX)) {
            e.push(
              new Error(
                `Start position X is not defined at circle index ${index}. Start Position X is the center x position of the circle.`,
              ),
            );
          }

          if (!isDefined(startPositionY)) {
            e.push(
              new Error(
                `Start position Y is not defined at circle index ${index}. Start Position Y is the center y position of the circle.`,
              ),
            );
          }
          const startBoundaryLines = [
            startBoundaryX,
            startBoundaryLineX,
            startBoundaryY,
            startBoundaryLineY,
          ];
          const definedBoundaryLines = startBoundaryLines.filter(line =>
            isDefined(line),
          );

          if (definedBoundaryLines.length === 0) {
            e.push(
              new Error(
                `A boundary line was not provided at circle index ${index}. Boundary lines are used to calculate radius of circle. `,
              ),
            );
          }

          if (definedBoundaryLines.length > 1) {
            e.push(
              new Error(
                `Too many boundary properties provided at circle index ${index}. Please only provide one option: startBoundaryX, startBoundaryLineX, startBoundaryY, startBoundaryLineY `,
              ),
            );
          }

          if (isDefined(startBoundaryX) && isDefined(endBoundaryY)) {
            e.push(
              new Error(
                `Must provide start and end boundaries on the same axis i.e. both X or both Y. \n Provided startBoundaryX and endBoundaryY at circle index ${index}`,
              ),
            );
          }
          if (isDefined(startBoundaryY) && isDefined(endBoundaryX)) {
            e.push(
              new Error(
                `Must provide start and end boundaries on the same axis i.e. both X or both Y. \n Provided startBoundaryY and endBoundaryX at circle index ${index}`,
              ),
            );
          }

          if (isDefined(startBoundaryLineX) && isDefined(endBoundaryLineY)) {
            e.push(
              new Error(
                `Must provide start and end boundaries on the same axis i.e. both X or both Y. \n Provided startBoundaryLineX and endBoundaryLineY at circle index ${index}`,
              ),
            );
          }
          if (isDefined(startBoundaryLineY) && isDefined(endBoundaryLineX)) {
            e.push(
              new Error(
                `Must provide start and end boundaries on the same axis i.e. both X or both Y. \n Provided startBoundaryLineY and endBoundaryLineX at circle index ${index}`,
              ),
            );
          }
        },
      );

      const allErrors = errors.flat();
      return allErrors.length === 0
        ? { validation: true }
        : { validation: false, errors: allErrors };
    } catch (error) {
      return { validation: false, errors: [error] };
    }
  }
}
